import { router, useForm } from '@inertiajs/react'
import { useEffect, type FormEvent } from 'react'

import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Select } from '@/components/ui/Select'
import { Label } from '@/components/ui/Label'
import { TextArea } from '@/components/ui/TextArea'
import { FieldError } from '@/components/feedback/FieldError'

export type ParticipantOption = {
  id: string
  name: string
}

export type PracticeMatchFormValues = {
  id?: string | null
  homeType: 'Team' | 'School'
  homeId: string | null
  awayType: 'Team' | 'School'
  awayId: string | null
  scheduledAt: string | null
  location: string | null
  notes: string | null
}

type FormProps = {
  values: PracticeMatchFormValues
  teams: ParticipantOption[]
  schools: ParticipantOption[]
  mode: 'create' | 'edit'
}

const PARTICIPANT_KIND_OPTIONS = [
  { value: 'Team', label: 'クラブチーム同士' },
  { value: 'School', label: '高校同士' },
] as const

export function Form({ values, teams, schools, mode }: FormProps) {
  const initialKind: 'Team' | 'School' = values.homeType ?? 'Team'

  const { data, setData, post, put, processing, errors, transform } = useForm({
    kind: initialKind,
    home_id: values.homeId ?? '',
    away_id: values.awayId ?? '',
    scheduled_at: toLocalInputValue(values.scheduledAt),
    location: values.location ?? '',
    notes: values.notes ?? '',
  })

  useEffect(() => {
    if (data.home_id && data.away_id && data.home_id === data.away_id) {
      setData('away_id', '')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.kind])

  const participants = data.kind === 'Team' ? teams : schools
  const awayCandidates = participants.filter((option) => option.id !== data.home_id)

  const submit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    transform((current) => ({
      practice_match: {
        home_type: current.kind,
        away_type: current.kind,
        home_id: current.home_id,
        away_id: current.away_id,
        scheduled_at: current.scheduled_at,
        location: current.location,
        notes: current.notes,
      },
    }))
    if (mode === 'create') {
      post('/admin/practice_matches', { preserveScroll: true })
    } else if (values.id) {
      put(`/admin/practice_matches/${values.id}`, { preserveScroll: true })
    }
  }

  return (
    <Card className="max-w-2xl space-y-6" padding="lg">
      <form onSubmit={submit} className="space-y-6">
        <div className="space-y-4">
          <div>
            <Label htmlFor="match-kind">対戦種別</Label>
            <Select
              id="match-kind"
              value={data.kind}
              onChange={(event) => {
                const next = event.target.value as 'Team' | 'School'
                setData('kind', next)
                setData('home_id', '')
                setData('away_id', '')
              }}
            >
              {PARTICIPANT_KIND_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </Select>
            <p className="mt-2 text-xs text-white/40">
              練習試合は同じ種類同士（クラブチーム同士／高校同士）でのみ登録できます。
            </p>
          </div>

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div>
              <Label htmlFor="match-home">ホーム</Label>
              <Select
                id="match-home"
                value={data.home_id}
                onChange={(event) => setData('home_id', event.target.value)}
              >
                <option value="">選択してください</option>
                {participants.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Select>
              <FieldError className="mt-2" message={errors.home_id} />
            </div>
            <div>
              <Label htmlFor="match-away">アウェー</Label>
              <Select
                id="match-away"
                value={data.away_id}
                onChange={(event) => setData('away_id', event.target.value)}
              >
                <option value="">選択してください</option>
                {awayCandidates.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.name}
                  </option>
                ))}
              </Select>
              <FieldError className="mt-2" message={errors.away_id} />
            </div>
          </div>

          <div>
            <Label htmlFor="match-scheduled-at">日時</Label>
            <Input
              id="match-scheduled-at"
              type="datetime-local"
              value={data.scheduled_at}
              onChange={(event) => setData('scheduled_at', event.target.value)}
            />
            <FieldError className="mt-2" message={errors.scheduled_at} />
          </div>

          <div>
            <Label htmlFor="match-location">会場</Label>
            <Input
              id="match-location"
              value={data.location}
              onChange={(event) => setData('location', event.target.value)}
              placeholder="例: 〇〇高校第1グラウンド"
            />
            <FieldError className="mt-2" message={errors.location} />
          </div>

          <div>
            <Label htmlFor="match-notes">備考</Label>
            <TextArea
              id="match-notes"
              value={data.notes}
              onChange={(event) => setData('notes', event.target.value)}
              placeholder="集合時間や持ち物などを共有"
              rows={4}
            />
            <FieldError className="mt-2" message={errors.notes} />
          </div>
        </div>

        <div className="flex items-center justify-end gap-3">
          <Button type="button" variant="ghost" onClick={() => router.get('/admin/practice_matches')}>
            キャンセル
          </Button>
          <Button type="submit" disabled={processing}>
            {mode === 'create' ? '練習試合を登録' : '練習試合を更新'}
          </Button>
        </div>
      </form>
    </Card>
  )
}

function toLocalInputValue(iso: string | null | undefined) {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => value.toString().padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}
